import React, { useState } from 'react';
import { Alert } from 'antd';
import { Button, Input } from 'components/common';
import { checkSsoProviders } from 'services/api';
import { usePost } from 'hooks';

const SsoProviderSelector = ({
  email,
  setEmail,
  onSsoRedirect,
  onUsePasswordLogin,
  isLoading,
}) => {
  const [ssoProviders, setSsoProviders] = useState([]);
  const [hasSsoEnabled, setHasSsoEnabled] = useState(false);
  const [requiresProviderSelection, setRequiresProviderSelection] = useState(
    false
  );
  const [ssoError, setSsoError] = useState(null);

  const [
    { res: ssoCheckResult, error: ssoCheckError, loading: checkingSSO },
    checkSSO,
  ] = usePost({ url: checkSsoProviders });

  const handleEmailSubmit = () => {
    if (!email || !email.includes('@')) {
      setSsoError('Please enter a valid email address');
      return;
    }

    setSsoError(null);
    checkSSO({ email });
  };

  // Handle SSO check result
  React.useEffect(() => {
    if (ssoCheckResult) {
      if (ssoCheckResult.hasSsoEnabled) {
        setHasSsoEnabled(true);
        setSsoProviders(ssoCheckResult.availableProviders);
        setRequiresProviderSelection(ssoCheckResult.requiresProviderSelection);

        // If only one provider and no selection required, redirect immediately
        if (
          !ssoCheckResult.requiresProviderSelection &&
          ssoCheckResult.availableProviders.length === 1
        ) {
          const provider = ssoCheckResult.availableProviders[0];
          onSsoRedirect(provider.authorizationUrl);
          return;
        }
      } else {
        // No SSO configured, proceed with password login
        setHasSsoEnabled(false);
        setSsoProviders([]);
        onUsePasswordLogin();
      }
    }
  }, [ssoCheckResult, onSsoRedirect, onUsePasswordLogin]);

  // Handle SSO check error
  React.useEffect(() => {
    if (ssoCheckError) {
      setSsoError('Error checking SSO providers. Please try again.');
      console.error('SSO check failed:', ssoCheckError);
    }
  }, [ssoCheckError]);

  const handleProviderSelect = provider => {
    onSsoRedirect(provider.authorizationUrl);
  };

  const handleUsePassword = () => {
    onUsePasswordLogin();
  };

  if (hasSsoEnabled && requiresProviderSelection && ssoProviders.length > 0) {
    return (
      <div className='sso-provider-selector'>
        <div className='email-display'>
          <strong>Email:</strong> {email}
        </div>
        <div className='provider-options'>
          <h3>Choose your login method:</h3>
          {ssoProviders.map((provider, index) => (
            <Button
              key={index}
              onClick={() => handleProviderSelect(provider)}
              disabled={isLoading}
              className='provider-button'
              style={{ margin: '8px 0', width: '100%' }}
            >
              Continue with {provider.providerName}
            </Button>
          ))}
          <div className='or-divider'>or</div>
          <div style={{ textAlign: 'center' }}>
            <Button
              type='link'
              onClick={handleUsePassword}
              disabled={isLoading}
            >
              Use password instead
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' && email && !checkingSSO && !isLoading) {
      handleEmailSubmit();
    }
  };

  return (
    <div className='email-sso-check'>
      <div>
        <div className='label'>Email</div>
        <Input
          disabled={isLoading || checkingSSO}
          onChange={value => setEmail(value)}
          onKeyPress={handleKeyPress}
          value={email}
          placeholder='Enter your email address'
        />
      </div>
      <Button
        disabled={isLoading || checkingSSO || !email}
        onClick={handleEmailSubmit}
        type='primary'
        loading={checkingSSO}
        style={{ width: '100%', marginTop: '16px' }}
      >
        {checkingSSO ? 'Checking...' : 'Continue'}
      </Button>

      {ssoError && <Alert message={ssoError} type='error' />}

      <div className='password-fallback' style={{ textAlign: 'center' }}>
        <Button type='link' onClick={handleUsePassword} disabled={isLoading}>
          Use password login instead
        </Button>
      </div>
    </div>
  );
};

export default SsoProviderSelector;
